import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
    Alert,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Chip,
    CircularProgress,
    Stack,
    Typography
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { logout } from '../allauth/lib/allauth';
import ApiClient from '../config/ApiClient';
import { AssignmentInvitationAction, AssignmentInvitationChannel, AssignmentInvitationRole } from '../object-actions/types/types';

interface InvitationPublicResponse {
    token: string;
    role: AssignmentInvitationRole;
    channel: AssignmentInvitationChannel;
    masked_destination: string | null;
    action: AssignmentInvitationAction;
    expires: string | null;
    is_expired: boolean;
    assignment_id: number;
    organization: {
        id: number | null;
        name: string | null;
        logo: string | null;
        logo_dark: string | null;
        slug: string | null;
    };
    athlete_display: string | null;
    inviter_display: string | null;
    redirect_url?: string;
    created_user?: boolean;
}

type ActionKind = 'accept' | 'decline' | 'not-me';

const TERMINAL_INVITATION_ACTIONS: AssignmentInvitationAction[] = [
    'accepted',
    'declined',
    'revoked',
    'not_me',
];

const isTerminalInvitationAction = (
    value: string | null | undefined,
): value is AssignmentInvitationAction =>
    !!value && TERMINAL_INVITATION_ACTIONS.includes(value as AssignmentInvitationAction);

const invitationErrorMessage = (code: string | null | undefined): string | null => {
    switch (code) {
        case 'expired':
            return 'This invitation has expired. Please ask the sender to issue a new one.';
        case 'accepted':
            return 'This invitation has already been accepted.';
        case 'declined':
            return 'This invitation has already been declined.';
        case 'revoked':
            return 'This invitation was revoked by the sender.';
        case 'not_me':
            return 'This invitation was marked as not being for you.';
        case 'slot_unavailable':
            return 'This role is no longer available on the assignment.';
        case 'email_mismatch':
            return 'Sign in with the email address this invitation was sent to, then accept again.';
        case 'not_found':
            return 'Invitation not found.';
        case 'already_actioned':
            return 'This invitation has already been accepted or declined.';
        default:
            return null;
    }
};

const roleLabel = (role: AssignmentInvitationRole): string => {
    switch (role) {
        case 'athlete': return 'Athlete';
        case 'coach': return 'Coach';
        case 'parent': return 'Guardian';
        default: return role;
    }
};

const AcceptInvitation: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);
    const [invitation, setInvitation] = useState<InvitationPublicResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState<ActionKind | null>(null);
    const [acceptedThisSession, setAcceptedThisSession] = useState(false);

    const fetchInvitation = async () => {
        if (!token) return;
        setLoading(true);
        try {
            const res = await ApiClient.get<InvitationPublicResponse>(
                `/api/assignment-invitations/${token}/`,
            );
            if (res.success && res.data) {
                setInvitation(res.data);
            } else {
                setError(formatError(res.error) || 'Invitation not found');
            }
        } catch (e: any) {
            setError(e?.message || 'Failed to load invitation');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInvitation();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const handleAction = async (action: ActionKind) => {
        if (!token) return;
        setSubmitting(action);
        try {
            const res = await ApiClient.post<InvitationPublicResponse>(
                `/api/assignment-invitations/${token}/${action}/`,
                {},
            );
            if (res.success && res.data) {
                setInvitation(res.data);
                if (action === 'accept') {
                    setAcceptedThisSession(true);
                    const isAthlete = res.data.role === 'athlete';
                    if (isAthlete) {
                        enqueueSnackbar(
                            'Welcome! Complete your profile, sign the waiver, then take your assessment. '
                            + 'You can invite coaches or guardians from Manage assignment on your program.',
                            { variant: 'success', autoHideDuration: 10000 },
                        );
                    } else {
                        enqueueSnackbar('Invitation accepted', { variant: 'success' });
                    }
                    const redirect =
                        res.data.redirect_url ||
                        (res.data.assignment_id
                            ? `/assignments/${res.data.assignment_id}/onboarding`
                            : '/account/payments');
                    setTimeout(() => navigate(redirect), isAthlete ? 1200 : 600);
                } else if (action === 'decline') {
                    enqueueSnackbar('Invitation declined. Please log out to protect your account.', { variant: 'info' });
                } else {
                    enqueueSnackbar('Thanks — marked as not you. Please log out to protect your account.', { variant: 'info' });
                }
            } else {
                const code = typeof res.errors === 'object' ? res.errors?.code : undefined;
                if (isTerminalInvitationAction(code)) {
                    setInvitation((prev) => (prev ? { ...prev, action: code } : prev));
                }
                const message = invitationErrorMessage(code) || formatError(res.error) || 'Failed to submit';
                enqueueSnackbar(message, { variant: isTerminalInvitationAction(code) ? 'info' : 'error' });
            }
        } catch (e: any) {
            enqueueSnackbar(e?.message || 'Failed to submit', { variant: 'error' });
        } finally {
            setSubmitting(null);
        }
    };

    const handleLogout = async () => {
        try {
            await logout();
        } catch (e) {
            // Fall back to the login screen if the logout call fails.
        } finally {
            navigate('/account/login');
        }
    };

    const state = useMemo(() => {
        if (!invitation) return null;
        const linkError = searchParams.get('error');
        if (linkError === 'expired') return 'expired';
        if (isTerminalInvitationAction(linkError)) return linkError;
        if (linkError === 'slot_unavailable') return 'slot_unavailable';
        if (linkError === 'email_mismatch') return 'email_mismatch';
        if (invitation.action === 'accepted') return 'accepted';
        if (invitation.action === 'declined') return 'declined';
        if (invitation.action === 'revoked') return 'revoked';
        if (invitation.action === 'not_me') return 'not_me';
        if (invitation.is_expired) return 'expired';
        return 'pending';
    }, [invitation, searchParams]);

    const linkErrorMessage = useMemo(() => {
        const code = searchParams.get('error');
        if (!code) return null;
        return invitationErrorMessage(code)
            ?? 'We could not complete your invitation automatically. You can accept or decline below.';
    }, [searchParams]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error || !invitation) {
        return (
            <Box sx={{ maxWidth: 520, mx: 'auto', mt: 6, px: 2 }}>
                <Alert severity="error">{error || 'Invitation not found'}</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 560, mx: 'auto', mt: 6, px: 2 }}>
            <Card>
                <CardContent>
                    <Stack alignItems="center" spacing={2} sx={{ mb: 2 }}>
                        {invitation.organization.logo && (
                            <img
                                src={invitation.organization.logo}
                                alt={invitation.organization.name || 'Organization'}
                                style={{ maxHeight: 72 }}
                            />
                        )}
                        <Typography variant="h5" align="center">
                            {invitation.organization.name || 'Strong Mind Strong Performance'}
                        </Typography>
                    </Stack>

                    {linkErrorMessage && state === 'pending' && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            {linkErrorMessage}
                        </Alert>
                    )}

                    {state === 'pending' && (
                        <>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                                {invitation.inviter_display
                                    ? <><strong>{invitation.inviter_display}</strong> has invited you</>
                                    : <>You have been invited</>}
                                {' '}to join
                                {invitation.athlete_display
                                    ? <> <strong>{invitation.athlete_display}</strong>'s assignment</>
                                    : <> an assignment</>}
                                {' '}as the <Chip size="small" label={roleLabel(invitation.role)} sx={{ mx: 0.5 }} />.
                            </Typography>
                            {invitation.masked_destination && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    Sent to {invitation.masked_destination} via {invitation.channel.toUpperCase()}
                                </Typography>
                            )}
                            {invitation.expires && (
                                <Typography variant="caption" color="text.secondary">
                                    Expires {new Date(invitation.expires).toLocaleString()}
                                </Typography>
                            )}
                        </>
                    )}

                    {state === 'accepted' && (
                        <Alert severity={acceptedThisSession ? 'success' : 'info'} sx={{ mt: 1 }}>
                            {acceptedThisSession
                                ? 'You have accepted this invitation. Redirecting…'
                                : invitationErrorMessage('accepted')}
                        </Alert>
                    )}
                    {state === 'declined' && (
                        <Alert severity="info" sx={{ mt: 1 }}>
                            You have declined this invitation. The sender has been notified. If you
                            were signed in on a shared device, please log out to protect your account.
                        </Alert>
                    )}
                    {state === 'not_me' && (
                        <Alert severity="info" sx={{ mt: 1 }}>
                            Thanks — we recorded that this invitation isn’t for you and notified the
                            sender. Please log out so the right person can sign in.
                        </Alert>
                    )}
                    {state === 'revoked' && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                            This invitation was revoked by the sender.
                        </Alert>
                    )}
                    {state === 'expired' && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                            {invitationErrorMessage('expired')}
                        </Alert>
                    )}
                    {state === 'slot_unavailable' && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                            {invitationErrorMessage('slot_unavailable')}
                        </Alert>
                    )}
                    {state === 'email_mismatch' && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                            {invitationErrorMessage('email_mismatch')}
                        </Alert>
                    )}
                </CardContent>

                {state === 'pending' && (
                    <CardActions sx={{ flexWrap: 'wrap', justifyContent: 'center', pb: 3, gap: 1 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={submitting === 'accept' ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                            onClick={() => handleAction('accept')}
                            disabled={!!submitting}
                            data-attr="accept-invitation_accept"
                        >
                            Accept
                        </Button>
                        <Button
                            variant="outlined"
                            color="warning"
                            startIcon={submitting === 'decline' ? <CircularProgress size={16} /> : <CancelIcon />}
                            onClick={() => handleAction('decline')}
                            disabled={!!submitting}
                            data-attr="accept-invitation_decline"
                        >
                            Decline
                        </Button>
                        <Button
                            variant="text"
                            startIcon={submitting === 'not-me' ? <CircularProgress size={16} /> : <HelpOutlineIcon />}
                            onClick={() => handleAction('not-me')}
                            disabled={!!submitting}
                            data-attr="accept-invitation_not-me"
                        >
                            Not me
                        </Button>
                    </CardActions>
                )}

                {(state === 'declined' || state === 'not_me') && (
                    <CardActions sx={{ justifyContent: 'center', pb: 3 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => void handleLogout()}
                            data-attr="accept-invitation_logout"
                        >
                            Log out
                        </Button>
                    </CardActions>
                )}
            </Card>
        </Box>
    );
};

function formatError(err: any): string {
    if (!err) return '';
    if (typeof err === 'string') return err;
    if (typeof err === 'object') {
        const flat = Object.values(err).flat();
        if (flat.length) return flat.join('; ');
    }
    return '';
}

export default AcceptInvitation;
